/**
 * Pure helpers that turn the per-provider billing-event counts from
 * `GET_USER_BILLING_EVENT_COUNTS` into the ordered provider tabs the `/user-billing` page
 * renders — one tab per provider whose count is > 0. For the 2nd-party providers that count
 * is already scoped to receipt-linked events (`hasReceipt: true` in the query), so a provider
 * with events but no matching receipt reports 0 and gets no tab. Card / PayPal / Adyen events
 * run through Zuora, so they are counted under the `zuora` alias and surface on the Zuora tab.
 * @module services/user/billing-subscription/group-billing-events
 */
import _get from 'lodash/get'

/** Provider keys — match the `GET_USER_BILLING_EVENT_COUNTS` aliases and the
 * `providerSubscription` GraphQL `provider` argument. */
export const PROVIDER_ZUORA = 'zuora'
export const PROVIDER_APPLE = 'itunes'
export const PROVIDER_GOOGLE = 'google'
export const PROVIDER_AMAZON = 'amazon'
export const PROVIDER_ROKU = 'roku'

/**
 * `BillingEvent.source` values folded into each provider tab — the source of truth the
 * `GET_USER_BILLING_EVENT_COUNTS` query mirrors. Card / PayPal / Adyen run through Zuora.
 */
export const SOURCE_TO_PROVIDER = {
  zuora: PROVIDER_ZUORA,
  paypal: PROVIDER_ZUORA,
  myo_paypal: PROVIDER_ZUORA,
  adyen: PROVIDER_ZUORA,
  apple: PROVIDER_APPLE,
  itunes: PROVIDER_APPLE,
  google: PROVIDER_GOOGLE,
  amazon: PROVIDER_AMAZON,
  roku: PROVIDER_ROKU,
}

/** Tab display order, Zuora first then receipt providers. */
export const PROVIDER_ORDER = [
  PROVIDER_ZUORA,
  PROVIDER_APPLE,
  PROVIDER_GOOGLE,
  PROVIDER_AMAZON,
  PROVIDER_ROKU,
]

/** Human-readable tab labels per provider key. */
export const PROVIDER_LABELS = {
  [PROVIDER_ZUORA]: 'Zuora',
  [PROVIDER_APPLE]: 'iTunes',
  [PROVIDER_GOOGLE]: 'Google',
  [PROVIDER_AMAZON]: 'Amazon',
  [PROVIDER_ROKU]: 'Roku',
}

/**
 * @typedef {Object} ProviderTabModel
 * @property {string} key Stable identity for the tab (the provider key)
 * @property {string} provider Provider key (see `PROVIDER_*`)
 * @property {boolean} isZuora Whether this tab renders the Zuora (card / PayPal / Adyen) UI
 * @property {string} label Display label, e.g. `iTunes`
 */

/**
 * Build the ordered provider tab list from per-provider billing-event counts.
 * A provider gets a tab when its count is > 0. A Zuora tab is always present when the user
 * has a Zuora billing account, even with a 0 Zuora count, because the Zuora tab content is
 * driven by the REST subscription/billing-account data rather than by billing events.
 *
 * @param {Object} options
 * @param {?Object} options.counts `GET_USER_BILLING_EVENT_COUNTS` result — `{ zuora: { count }, itunes: { count }, … }`
 * @param {boolean} options.hasBillingAccount Whether the user has a Zuora `billing_account_id`
 * @returns {ProviderTabModel[]} Ordered tab models; empty when there is nothing to show
 */
export function buildProviderTabModels ({ counts, hasBillingAccount } = {}) {
  const models = PROVIDER_ORDER.filter((provider) => _get(counts, [provider, 'count'], 0) > 0).map(
    (provider) => ({
      key: provider,
      provider,
      isZuora: provider === PROVIDER_ZUORA,
      label: PROVIDER_LABELS[provider],
    }),
  )

  if (hasBillingAccount && !models.some((model) => model.isZuora)) {
    models.unshift({
      key: PROVIDER_ZUORA,
      provider: PROVIDER_ZUORA,
      isZuora: true,
      label: PROVIDER_LABELS[PROVIDER_ZUORA],
    })
  }

  return models
}
